import React, { useMemo, useState } from 'react';
import { Chart } from 'primereact/chart';
import Tabs, { TabConfig } from '../../components/reusables/Tabs';
import DropDown, { DropDownOption } from '../../components/reusables/DropDown';
import { type ColumnDef } from '@tanstack/react-table';
import { Button } from '@appkit4/react-components/button';
import Pagination from '../../components/reusables/Pagination';
import DataTable from '../../components/reusables/DataTable';

const companyOptions: DropDownOption[] = [
    { label: 'All companies', value: 'all' },
    { label: 'Company A', value: 'company-a' },
    { label: 'Company B', value: 'company-b' },
];

const tabsConfig: TabConfig[] = [
    { label: 'Dashboard', value: 'dashboard' },
    { label: 'Reports', value: 'reports' },
];

type Report = {
    id: number;
    reportName: string;
    reportType: string;
    description: string;
};

const reportsData: Report[] = [
    {
        id: 1,
        reportName:
            'Ergofy:Ergofy monthly active user licence For the month Fe...',
        reportType: 'Currys',
        description: '2023-11-10',
    },
    {
        id: 2,
        reportName: 'TRADE ACCESS PAN BDC...',
        reportType: 'Softcat PLC',
        description: '2023-11-10',
    },
    {
        id: 3,
        reportName: '4No Canon DX 3720I - 48 month lease @ £798.80 Per Qua...',
        reportType: 'Currys',
        description: '2023-11-10',
    },
    {
        id: 4,
        reportName: 'AVPARTMASTER ON 03 FEB BDC',
        reportType: 'Currys',
        description: '2023-11-10',
    },
    {
        id: 5,
        reportName:
            'Ergofy:Ergofy monthly active user licence For the month Fe...',
        reportType: 'P F Cusack Supplies Ltd',
        description: '2023-11-10',
    },
    {
        id: 6,
        reportName: 'TRADE ACCESS PAN B...',
        reportType: 'Dell Corporation Limited',
        description: '2023-11-10',
    },
    {
        id: 7,
        reportName: '4No Canon DX 3720I - 48 month lease @ £798.80 Per Qua...',
        reportType: 'Evaris',
        description: '2023-11-10',
    },
    {
        id: 8,
        reportName: 'AVPARTMASTER ON 03 FEB BDC',
        reportType: 'Amazon UK Marketplace',
        description: '2023-11-10',
    },
    {
        id: 9,
        reportName: 'AVPARTMASTER ON 03 FEB BDC',
        reportType: 'Apple Retail UK Limited',
        description: '2023-11-10',
    },
    {
        id: 10,
        reportName: 'AVPARTMASTER ON 03 FEB BDC',
        reportType: 'Apple Retail UK Limited',
        description: '2023-11-10',
    },
];

const itemsPerPageOptions: DropDownOption[] = [
    { label: '10', value: '10' },
    { label: '25', value: '25' },
    { label: '50', value: '50' },
];

const Reporting: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string>('dashboard');
    const [selectedCompany, setSelectedCompany] = useState<string>('all');
    const [minValue, setMinValue] = useState<string>('');
    const [maxValue, setMaxValue] = useState<string>('');

    // Reports tab state
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<string>('25');
    const [sortActive, setSortActive] = useState<string>('reportName');
    const [sortPhase, setSortPhase] = useState<number>(0);

    // Dashboard chart data
    const stackedBarData = useMemo(
        () => ({
            labels: [
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '7',
                '8',
                '9',
                '10',
                '11',
                '12',
                '13',
                '14',
                '15',
                '16',
                '17',
                '18',
                '19',
                '20',
                '21',
                '22',
            ],
            datasets: [
                {
                    label: 'Dark blue',
                    backgroundColor: '#FFAA72',
                    data: [
                        20, 18, 25, 22, 24, 19, 21, 23, 18, 21, 19, 20, 22, 23,
                        21, 24, 23, 25, 22, 21, 23, 24,
                    ],
                    borderRadius: 4,
                },
                {
                    label: 'Light blue',
                    backgroundColor: '#ffd0b3',
                    data: [
                        10, 12, 9, 8, 7, 11, 9, 7, 12, 10, 13, 10, 9, 7, 11, 8,
                        7, 5, 9, 10, 8, 7,
                    ],
                    borderRadius: 4,
                },
            ],
        }),
        []
    );

    const stackedBarOptions = useMemo(
        () => ({
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                legend: {
                    display: true,
                    position: 'top' as const,
                    labels: {
                        usePointStyle: true,
                        boxWidth: 8,
                        color: '#333',
                        font: { size: 11 },
                    },
                },
                tooltip: { enabled: true },
            },
            scales: {
                x: {
                    stacked: true,
                    grid: { display: false },
                    ticks: { color: '#666', font: { size: 10 } },
                },
                y: {
                    stacked: true,
                    grid: { color: '#f3f3f3' },
                    ticks: { color: '#666', font: { size: 10 } },
                    border: { display: false },
                },
            },
        }),
        []
    );

    const doughnutData = useMemo(
        () => ({
            labels: ['Pool A', 'Pool B', 'Pool C'],
            datasets: [
                {
                    data: [40, 35, 25],
                    backgroundColor: ['#FFAA72', '#ff9340', '#ffd0b3'],
                    hoverBackgroundColor: ['#ff9a5c', '#f27f32', '#ffbf99'],
                    borderWidth: 0,
                },
            ],
        }),
        []
    );

    const doughnutOptions = useMemo(
        () => ({
            cutout: '70%',
            plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
            },
        }),
        []
    );

    // Handlers
    const handleTabChange = (_index: number, value: string) => {
        setActiveTab(value);
    };

    const handleApplyFilters = () => {
        console.log('Apply filters', {
            company: selectedCompany,
            minValue,
            maxValue,
        });
    };

    const handleResetRules = () => {
        setSelectedCompany('all');
        setMinValue('');
        setMaxValue('');
    };

    const reportColumns: ColumnDef<Report, any>[] = [
        {
            accessorKey: 'reportName',
            header: 'Report Name',
            enableSorting: true,
        },
        {
            accessorKey: 'reportType',
            header: 'Report Type',
            enableSorting: true,
        },
        {
            accessorKey: 'description',
            header: 'Description',
            enableSorting: true,
        },
        {
            id: 'download',
            header: '',
            enableSorting: false,
            cell: ({ row }) => (
                <Button
                    kind="text"
                    icon="icon-download-outline"
                    compact
                    onClick={() => handleDownload(row.original)}
                    ariaLabel="Download report"
                />
            ),
        },
    ];

    const handleDownload = (report: Report) => {
        console.log('Download report', report);
    };

    // Pagination
    const totalItems = 250;
    const totalPages = Math.ceil(totalItems / Number(itemsPerPage));

    const handlePrevPage = () => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
    };

    const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const page = Number(e.target.value);
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <div className="reporting-dashboard">
            <div className="reporting-dashboard__header">
                <h1 className="reporting-dashboard__title">Reporting</h1>
            </div>
            <div className="reporting-dashboard__tabs">
                <Tabs
                    tabs={tabsConfig}
                    defaultActiveIndex={0}
                    onChange={handleTabChange}
                />
            </div>

            {/* ==================== DASHBOARD TAB ==================== */}
            {activeTab === 'dashboard' && (
                <>
                    <div className="reporting-dashboard__filters">
                        <DropDown
                            data={companyOptions}
                            value={selectedCompany}
                            onSelect={(value) =>
                                setSelectedCompany(
                                    Array.isArray(value)
                                        ? (value[0] ?? 'all')
                                        : value
                                )
                            }
                            placeholder="Company"
                        />
                        <input
                            type="number"
                            className="text-input"
                            value={minValue}
                            onChange={(e) => setMinValue(e.target.value)}
                            placeholder="Min"
                        />
                        <input
                            type="number"
                            className="text-input"
                            value={maxValue}
                            onChange={(e) => setMaxValue(e.target.value)}
                            placeholder="Max"
                        />
                        <div className="reporting-dashboard__filter-buttons">
                            <Button kind="primary" onClick={handleApplyFilters}>
                                Apply Filters
                            </Button>
                            <Button kind="secondary" onClick={handleResetRules}>
                                Reset Rules
                            </Button>
                        </div>
                    </div>

                    <div className="reporting-dashboard__content">
                        <div className="kpi-group">
                            <div className="kpi-group__item">
                                <div className="kpi-group__label">
                                    Total companies
                                </div>
                                <div className="kpi-group__value">2</div>
                            </div>
                            <div className="kpi-group__separator" />
                            <div className="kpi-group__item">
                                <div className="kpi-group__label">
                                    Total number of projects analysed
                                </div>
                                <div className="kpi-group__value">0</div>
                            </div>
                            <div className="kpi-group__separator" />
                            <div className="kpi-group__item">
                                <div className="kpi-group__label">
                                    Total expenditure analysed
                                </div>
                                <div className="kpi-group__value">73M</div>
                            </div>
                            <div className="kpi-group__separator" />
                            <div className="kpi-group__item">
                                <div className="kpi-group__label">
                                    Total first year tax value
                                </div>
                                <div className="kpi-group__value">1M</div>
                            </div>
                        </div>

                        <div className="kpi-side">
                            <div className="kpi-side__label">
                                Total qualifying expenditure
                            </div>
                            <div className="kpi-side__value">11M</div>
                        </div>

                        <div className="chart-panel chart-panel--bar">
                            <Chart
                                type="bar"
                                data={stackedBarData}
                                options={stackedBarOptions}
                                style={{ width: '100%', height: '100%' }}
                            />
                        </div>

                        <div className="kpi-side">
                            <div className="kpi-side__label">
                                Total estimated first year deduction
                            </div>
                            <div className="kpi-side__value">7M</div>
                        </div>

                        <div className="chart-panel chart-panel--doughnut">
                            <div className="chart-panel__header">
                                Capital allowances analysis
                            </div>
                            <div className="chart-panel__body">
                                <Chart
                                    type="doughnut"
                                    data={doughnutData}
                                    options={doughnutOptions}
                                    style={{
                                        width: '100%',
                                        maxWidth: '220px',
                                        margin: '0 auto',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ==================== REPORTS TAB ==================== */}
            {activeTab === 'reports' && (
                <div className="reports-tab">
                    <DataTable
                        data={reportsData}
                        columns={reportColumns}
                        sorting
                    />

                    <div className="rules-pagination">
                        <Pagination />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reporting;
